/* ------------------------------------------------------------------ */
/* NewAuction.tsx – Clean, builds, participants always saved */
/* ------------------------------------------------------------------ */
import React, { useMemo, useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { format, isToday } from "date-fns";
import {
  Calendar,
  Clock,
  Users,
  FileText,
  Upload,
  Plus,
  Trash2,
  IndianRupee,
  ArrowDown,
  ArrowUp,
  Eye,
  Lock,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import AuctionService from "../../../services/newAuctionService";
import auctionViewerService from "../../../services/auctionViewerService";
import CompanyViewerFields, {
  EMPTY_VIEWER,
} from "../SharedAuctions/CompanyViewerFields";
import { CreateAuctionRequest } from "../../../types/auction";
import SuccessModal from "../../Common/SuccessModal";
import ErrorModal from "../../Common/ErrorModal";
import InfoModal from "../../Common/InfoModal";
import "./NewAuction.css";

/* -------------------------- types --------------------------------- */
interface Participant {
  companyName: string;
  companyAddress: string;
  personName: string;
  mailId: string;
  contactNumber: string;
  _quick?: boolean;
  _known?: boolean; // email matched a registered user; names come from the DB
  _legacy?: boolean; // invited by phone only, before email invites; kept as is
}

type AuctionTypeValue = "english_no_tie" | "english" | "traffic_light";
type AuctionModeValue = "reverse" | "forward";

interface AuctionForm {
    title: string;
  auctionDate: string;
  auctionStartTime: string;
  duration: number;
  currency: "INR" | "USD";
  auctionMode: AuctionModeValue;
  auctionType: AuctionTypeValue;
  auctionDetails: string;
  decrementalValue?: number;
  participants: Participant[];
}

/* ------------------------ constants ------------------------------- */
const PHONE_REGEX = /^(\+91)?[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MAX_FILES = 3;
const MAX_FILE_MB = 15; // Per file limit
const MAX_TOTAL_MB = 40; // Total upload size limit

/* ================================================================== */
const NewAuction: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Full-screen format picker shown first; the form opens once one is chosen.
  const [formatChosen, setFormatChosen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Same form serves both creating and editing. /edit-auction/:id puts it in
  // edit mode, which is only possible until the auction goes live.
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = !!editId;
  const [editLoading, setEditLoading] = useState(isEdit);
  const [editBlocked, setEditBlocked] = useState<string | null>(null);
  const [existingDocs, setExistingDocs] = useState<{ id: number; file_name: string }[]>([]);
  const [removedDocIds, setRemovedDocIds] = useState<number[]>([]);
  const keptDocs = existingDocs.filter((d) => !removedDocIds.includes(d.id));
  const todayISO = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
    /* -------------- react-hook-form -------------- */
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
    setError,
    clearErrors,
  } = useForm<AuctionForm>({
    defaultValues: {
      title: "",
      auctionDate: todayISO,
      auctionStartTime: format(new Date(), "HH:mm"),
      duration: 120,
      currency: "INR",
      auctionMode: "reverse",
      auctionType: "english_no_tie",
      auctionDetails: "",
      participants: [],
    },
    mode: "onBlur", // Show errors as soon as user leaves a field
    reValidateMode: "onChange", // Re-validate on change after first error
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "participants",
  });

  // Channel partners can attach the company's own contact (the "2nd
  // auctioneer") to the auction. That person gets a view-only live panel. It's
  // kept out of react-hook-form on purpose: it isn't part of the auction
  // payload, it's a separate call made once the auction exists.
  const isChannelPartner = user?.role === "channel_partner";
  const [viewer, setViewer] = useState(EMPTY_VIEWER);
  const [viewerErrors, setViewerErrors] = useState<Record<string, string>>({});

  const [newParticipantEmail, setNewParticipantEmail] = useState("");
  const [addingParticipants, setAddingParticipants] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [successModal, setSuccessModal] = useState({
       isOpen: false,
    message: "",
  });
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    message: "",
  });
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    message: "",
  });
  const [resetModal, setResetModal] = useState({
    isOpen: false,
  });

  const watchAuctionDate = watch("auctionDate");
  const watchAuctionStartTime = watch("auctionStartTime");
  const watchAuctionMode = watch("auctionMode");
  const watchAuctionType = watch("auctionType");
  const watchParticipants = watch("participants");

  // Emails currently in the list, including edits typed into a card
  const currentParticipantEmails = (): Set<string> =>
    new Set(
      (watchParticipants || [])
        .map((f) => (f?.mailId || "").trim().toLowerCase())
        .filter(Boolean)
    );
  const isForwardAuction = watchAuctionMode === "forward";
    /* -------------- side-effects -------------- */
  React.useEffect(() => {
    if (!watchAuctionDate || !watchAuctionStartTime) return;

    const selectedDate = new Date(watchAuctionDate);
    if (!isToday(selectedDate)) {
      clearErrors("auctionStartTime");
      return;
    }

    const now = format(new Date(), "HH:mm");
    if (watchAuctionStartTime < now) {
      setError("auctionStartTime", {
        type: "manual",
        message: "Start time must be in the future for today's date",
      });
    } else {
      clearErrors("auctionStartTime");
    }
  }, [watchAuctionDate, watchAuctionStartTime, setError, clearErrors]);

  /* -------------- helpers -------------- */
  const normalizedPhone = (raw: string): string => {
    const trimmed = raw.trim();
    const noLeadingZeros = trimmed.startsWith("0")
      ? trimmed.replace(/^0+/, "")
      : trimmed;
    let candidate = noLeadingZeros.startsWith("+")
      ? noLeadingZeros
      : noLeadingZeros;
    if (!candidate.startsWith("+91")) {
      if (/^[6-9]\d{9}$/.test(candidate)) candidate = `+91${candidate}`;
    }

    return candidate;
  };
  
  // Edit mode: load the auction, make sure it is still editable, pre-fill.
  React.useEffect(() => {
    if (!isEdit || !editId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await AuctionService.getAuctionById(editId);
        const a = res?.auction;
        if (cancelled) return;
        if (!res?.success || !a) throw new Error(res?.message || "Auction not found");

        if (!a.is_creator) {
          setEditBlocked("Only the auctioneer who created this auction can edit it.");
          return;
        }
        if (a.status !== "upcoming" && a.status !== "draft") {
          setEditBlocked(
            `This auction is ${a.status}, so it can no longer be edited. Auctions can only be changed before they go live.`
          );
          return;
        }

        reset({
          title: a.title || "",
          auctionDate: String(a.auction_date || "").split("T")[0],
          auctionStartTime: String(a.start_time || "").slice(0, 5),
          duration: Number(a.duration) || 120,
          currency: a.currency === "USD" ? "USD" : "INR",
          auctionMode: a.auction_mode === "forward" ? "forward" : "reverse",
          auctionType: a.auction_type || "english_no_tie",
          auctionDetails: a.description || "",
          decrementalValue: Number(a.decremental_value) || undefined,
          participants: (a.participants || []).map((p: any) => ({
            companyName: p.company_name || "",
            companyAddress: "",
            personName: p.person_name || "",
            mailId: String(p.invite_email || p.user_email || "").trim().toLowerCase(),
            contactNumber: p.phone_number
              ? `+91${String(p.phone_number).replace(/\D/g, "").slice(-10)}`
              : "",
            _quick: true,
            _known: !!(p.person_name || p.company_name),
            _legacy: !(p.invite_email || p.user_email),
          })),
        });
        setExistingDocs(
          (a.documents || []).map((d: any) => ({ id: Number(d.id), file_name: d.file_name }))
        );
        setFormatChosen(true);
      } catch (e: any) {
        if (!cancelled) setEditBlocked(e?.message || "Could not load this auction.");
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editId]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Allowed file types
    const allowedExtensions = [
      ".jpeg",
      ".jpg",
      ".png",
      ".pdf",
      ".doc",
      ".docx",
    ];

    // Filter allowed file types
    const typeFiltered = files.filter((f) => {
      const ext = f.name.toLowerCase().slice(f.name.lastIndexOf("."));
      if (!allowedExtensions.includes(ext)) {
        setInfoModal({
          isOpen: true,
          message: `"${f.name}" is not an allowed file type. Please upload JPEG, JPG, PNG, PDF, DOC, or DOCX files only.`,
        });
        return false;
      }
      return true;
    });
   // Filter out files exceeding size
    const validFiles = typeFiltered.filter((f) => {
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        setInfoModal({
          isOpen: true,
          message: `"${f.name}" exceeds the maximum file size of ${MAX_FILE_MB} MB. Please select a smaller file.`,
        });
        return false;
      }
      return true;
    });

    // Deduplicate based on name + size
    const dedup = validFiles.filter(
      (f) => !uploadedFiles.some((u) => u.name === f.name && u.size === f.size)
    );

    // Check file count limit
    if (keptDocs.length + uploadedFiles.length + dedup.length > MAX_FILES) {
      setInfoModal({
        isOpen: true,
        message: `Maximum ${MAX_FILES} files allowed. Please remove some files before adding more.`,
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Check total size limit
    const currentTotalSize = uploadedFiles.reduce((sum, f) => sum + f.size, 0);
    const newFilesSize = dedup.reduce((sum, f) => sum + f.size, 0);
    const totalSizeMB = (currentTotalSize + newFilesSize) / (1024 * 1024);
    
    if (totalSizeMB > MAX_TOTAL_MB) {
      setInfoModal({
        isOpen: true,
        message: `Total upload size (${totalSizeMB.toFixed(2)} MB) exceeds the maximum limit of ${MAX_TOTAL_MB} MB. Please remove some files or select smaller files.`,
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Update state if valid files found
    if (dedup.length > 0) {
      setUploadedFiles((prev) => [...prev, ...dedup]);
    }

    // Reset input so user can re-select the same file again
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx: number) => {
    setUploadedFiles((p) => p.filter((_, i) => i !== idx));
  };

  // Resolve saved names for email addresses. A failed lookup must never block
  // adding a participant -- they just show as "not registered".
  const lookupKnown = async (emails: string[]) => {
    try {
      return await AuctionService.lookupParticipants(emails);
    } catch (e) {
      console.warn("Participant lookup failed:", e);
      return {};
    }
  };

  const participantFromLookup = (
    email: string,
    known: Record<string, { person_name: string; company_name: string }>
  ): Participant => {
    const hit = known[email];
    return {
      companyName: hit?.company_name || "",
      companyAddress: "",
      personName: hit?.person_name || "",
      mailId: email,
      contactNumber: "",
      _quick: true,
      _known: !!(hit && (hit.person_name || hit.company_name)),
    };
  };

  const isOwnEmail = (email: string) =>
    !!user?.email && user.email.trim().toLowerCase() === email;

  const addParticipantByEmail = async () => {
    const email = newParticipantEmail.trim().toLowerCase();
    if (!EMAIL_REGEX.test(email)) {
      setInfoModal({
        isOpen: true,
        message: "Please enter a valid email address (e.g., name@company.com).",
      });
      return;
    }
    if (isOwnEmail(email)) {
      setInfoModal({
        isOpen: true,
        message: "You cannot add your own email as a participant.",
      });
      return;
    }
    if (currentParticipantEmails().has(email)) {
      setInfoModal({
        isOpen: true,
        message:
          "This email has already been added to the participants list.",
      });
      return;
    }
    if (addingParticipants) return;
    setAddingParticipants(true);
    try {
      const known = await lookupKnown([email]);
      append(participantFromLookup(email, known));
      setNewParticipantEmail("");
    } finally {
      setAddingParticipants(false);
    }
  };

  const handleReset = () => {
    reset({
      title: "",
      auctionDate: todayISO,
      auctionStartTime: format(new Date(), "HH:mm"),
      duration: 120,
      currency: "INR",
      auctionMode: "reverse",
      auctionType: "english_no_tie",
      auctionDetails: "",
      participants: [],
    });
    setUploadedFiles([]);
    setViewer(EMPTY_VIEWER);
    setViewerErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
    setResetModal({ isOpen: false });
  };

  /* -------------- validation helpers -------------- */
  const validateFormAndShowErrors = (data: AuctionForm): boolean => {
    const missingFields: string[] = [];

    // Check required fields
    if (!data.title?.trim()) {
      missingFields.push("Auction Title");
    }
    if (!data.auctionDate) {
      missingFields.push("Auction Date");
    }
    if (!data.auctionStartTime) {
      missingFields.push("Start Time");
    }
    if (!data.auctionDetails?.trim()) {
      missingFields.push("Auction Details");
    }
    if (!data.decrementalValue || data.decrementalValue < 1) {
      missingFields.push(
        data.auctionMode === "forward" ? "Incremental Value" : "Decremental Value"
      );
    }

    // Check participants - always required. Participants invited by phone
    // before email invites existed have no email and are kept as they are.
    const validParticipants = data.participants.filter(
      (p) =>
        p._legacy || EMAIL_REGEX.test((p.mailId || "").trim())
    );

    if (validParticipants.length === 0) {
      missingFields.push("At least one valid participant");
    }

    data.participants.forEach((participant, index) => {
      if (!participant._legacy && !EMAIL_REGEX.test((participant.mailId || "").trim())) {
        missingFields.push(`Valid Email for Participant ${index + 1}`);
      }
    });
      // Show error if there are missing fields
    if (missingFields.length > 0) {
      let errorMessage = "Please fill in the following required fields: ";
      if (missingFields.length === 1) {
        errorMessage += missingFields[0];
      } else {
        errorMessage +=
          missingFields.slice(0, -1).join(", ") +
          " and " +
          missingFields[missingFields.length - 1];
      }
      setErrorModal({
        isOpen: true,
        message: errorMessage,
      });
      return false;
    }

    return true;
  };
  // The company viewer is optional and needs only an email. This runs
  // before the auction is created so a bad number can't leave behind an
  // auction whose viewer then fails to attach.
  const viewerTouched = !!viewer.email.trim();

  const validateViewer = (data: AuctionForm): boolean => {
    if (!isChannelPartner || !viewerTouched) {
      setViewerErrors({});
      return true;
    }

    const errs: Record<string, string> = {};
    const email = viewer.email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(email)) {
      errs.email = "Enter a valid email address";
    } else if (isOwnEmail(email)) {
      errs.email = "You cannot add your own email as the company viewer";
    } else if (
      data.participants.some((p) => (p.mailId || "").trim().toLowerCase() === email)
    ) {
      errs.email = "This email is already a bidding participant, so it can't also be the viewer";
    }

    setViewerErrors(errs);
    if (Object.keys(errs).length > 0) {
      setErrorModal({
        isOpen: true,
        message: `Please fix the company viewer details: ${Object.values(errs).join("; ")}`,
      });
      return false;
    }
    return true;
  };

   /* -------------- submit -------------- */
  const onSubmit = async (data: AuctionForm) => {
    if (isSubmitting) return;

    if (!user || !AuctionService.isAuthenticated()) {
      setErrorModal({
        isOpen: true,
        message: "Please log in to continue",
      });
      navigate("/login");
      return;
    }

    // Validate form and show specific field errors
    if (!validateFormAndShowErrors(data)) {
      return;
    }
    if (!validateViewer(data)) {
      return;
    }

    setIsSubmitting(true);

    try {
      /* 1. participant emails – always build (phone-only legacy rows are
            kept by the server and are not sent) */
      const emails = data.participants
        .filter((p) => !p._legacy)
        .map((p) => (p.mailId || "").trim().toLowerCase())
        .filter((e) => EMAIL_REGEX.test(e));
      const uniqueEmails = Array.from(new Set(emails));

      /* 2. validate participants required */
      if (uniqueEmails.length === 0 && !data.participants.some((p) => p._legacy)) {
        setErrorModal({
          isOpen: true,
          message: 'Please add at least one participant',
        });
        setIsSubmitting(false);
        return;
      }

      const startTime =
        data.auctionStartTime.length === 5
          ? `${data.auctionStartTime}:00`
          : data.auctionStartTime;

      /* 3. payload – always send the list */
      const payload: CreateAuctionRequest = {
        title: data.title.trim(),
        description: data.auctionDetails.trim(),
        auction_date: data.auctionDate,
        start_time: startTime,
        duration: data.duration,
        currency: data.currency,
        base_price: 0,
        decremental_value: data.decrementalValue ?? 0,
        pre_bid_allowed: true,
        auction_type: data.auctionType,
        auction_mode: data.auctionMode,
        send_invitations: !isEdit,
        participants: uniqueEmails,
      };
      if (isEdit && editId) {
        const updated = await AuctionService.updateAuction(
          editId,
          payload,
          uploadedFiles,
          removedDocIds
        );
        if (!updated.success) throw new Error(updated.message || "Update failed");

        setSuccessModal({
          isOpen: true,
          message: `Auction "${data.title.trim()}" has been updated.`,
        });
        setTimeout(() => navigate(`/dashboard/my-auction/${editId}`), 1500);
        return;
      }

  const res = await AuctionService.createAuction(payload, uploadedFiles);
      if (!res.success) throw new Error(res.message || "Creation failed");

      // Create custom success message
      const participantCount = uniqueEmails.length;
      let successMessage = `Auction "${res.auction.title}" has been created successfully!`;
      successMessage += ` Invitations sent to ${participantCount} participant${
        participantCount > 1 ? "s" : ""
      }.`;

      // Attach the company viewer now that the auction exists. A failure here
      // must not read as "auction creation failed" -- the auction is real --
      // so it's reported in the message and can be fixed from the auction page.
      if (isChannelPartner && viewerTouched) {
        try {
          const result = await auctionViewerService.setForAuction(res.auction.id, {
            email: viewer.email.trim().toLowerCase(),
          });
          successMessage += ` The company viewer (${viewer.email.trim()}) can now watch it live, view-only.`;
          if (result.notifications.email === "failed") {
            successMessage += " We couldn't notify them automatically, so please share the login details yourself.";
          }
        } catch (viewerErr: any) {
          successMessage += ` However, the company viewer could not be added (${
            viewerErr?.message || "unknown error"
          }). Open the auction to add them again.`;
        }
      }

      // Show success modal
      setSuccessModal({
        isOpen: true,
        message: successMessage,
      });
         // Reset form
      reset({
        title: "",
        auctionDate: todayISO,
        auctionStartTime: format(new Date(), "HH:mm"),
        duration: 120,
        currency: "INR",
        auctionMode: "reverse",
        auctionType: "english_no_tie",
        auctionDetails: "",
        participants: [],
      });
      setUploadedFiles([]);
      setViewer(EMPTY_VIEWER);
      setViewerErrors({});

      // Navigate after modal is closed
      setTimeout(
        () => navigate(`/dashboard/my-auction/${res.auction.id}`),
        2000
      );
    } catch (e: any) {
      console.error(e);
      setErrorModal({
        isOpen: true,
        message: e.message || "Failed to create auction. Please try again.",
      });
      if (/auth|session/i.test(e.message)) {
        AuctionService.clearAuth();
        setTimeout(() => navigate("/login"), 2000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  /* -------------- render -------------- */
  if (isEdit && (editLoading || editBlocked)) {
    return (
      <div className="ap-newauction-wrapper">
        <div className="ap-newauction-container">
          <div className="card mb-6">
            <div className="card-body" style={{ textAlign: "center", padding: "2.5rem 1.5rem" }}>
              {editLoading ? (
                <>
                  <div className="loading-spinner" style={{ margin: "0 auto 1rem" }} />
                  <p className="text-text-secondary">Loading auction...</p>
                </>
              ) : (
                <>
                  <Lock className="w-10 h-10" style={{ margin: "0 auto 0.75rem", color: "var(--text-secondary)" }} />
                  <h1 className="text-xl font-bold text-text-primary">This auction can't be edited</h1>
                  <p className="text-text-secondary" style={{ margin: "0.5rem 0 1.25rem" }}>
                    {editBlocked}
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate(editId ? `/dashboard/my-auction/${editId}` : "/dashboard/MyCreatedA")}
                  >
                    <ArrowLeft className="w-4 h-4 inline mr-2" />
                    Back to Auction
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!formatChosen) {
    const pick = (mode: AuctionModeValue) => {
      setValue("auctionMode", mode, { shouldValidate: true });
      setFormatChosen(true);
    };
    return (
      <div className="ap-newauction-wrapper">
        <div className="ap-newauction-container">
          <div className="card mb-6">
            <div className="card-header">
              <h1 className="text-2xl font-bold text-text-primary">Create New Auction</h1>
              <p className="text-text-secondary text-sm">Choose the auction format to continue</p>
            </div>
            <div className="card-body">
              <div className="format-picker-grid">
                <button type="button" className="format-picker-option" onClick={() => pick("reverse")}>
                  <span className="format-picker-icon"><ArrowDown /></span>
                  <span className="format-picker-name">Reverse Auction</span>
                  <span className="format-picker-desc">Bidders compete downward &mdash; the lowest bid wins.</span>
                </button>
                <button type="button" className="format-picker-option" onClick={() => pick("forward")}>
                  <span className="format-picker-icon"><ArrowUp /></span>
                  <span className="format-picker-name">Forward Auction</span>
                  <span className="format-picker-desc">Bidders compete upward &mdash; the highest bid wins.</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-newauction-wrapper">
      <div className="ap-newauction-container">
        <div className="card mb-6">
          <div className="card-header flex items-center justify-between">
            <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
              {isEdit ? "Edit Auction" : "Create New Auction"}
            </h1>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent text-white text-sm font-medium">
              <Users className="w-4 h-4 mr-2" />
            </span>
          </div>
          <div className="card-body">
            <p className="text-text-secondary text-sm">
              {isEdit
                ? "You can change anything until the auction goes live. After that it is locked."
                : "Set up a new auction with participants and terms"}
            </p>
          </div>
        </div>
           <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-text-primary">
                Auction Information
              </h2>
              <p className="text-text-secondary">
                Schedule, duration and bidding format
              </p>
            </div>
          <div className="card-body space-y-6">
            {/* First Row: Title, Date, Time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="form-group">
                <label className="form-label">
                  <FileText className="w-4 h-4 inline mr-2" />
                  <span>
                    Auction Title<span className="required">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  className={`form-input ${
                    errors.title ? "form-input-error" : ""
                  }`}
                  placeholder="Enter Auction Title"
                  {...register("title", {
                    required: "Auction title is required and cannot be empty"
                  })}
                />
                {errors.title && (
                  <p className="form-error">{errors.title.message}</p>
                )}
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  <span>
                    Auction Date<span className="required">*</span>
                  </span>
                </label>
                <input
                  type="date"
                  min={todayISO}
                  className={`form-input ${
                    errors.auctionDate ? "form-input-error" : ""
                  }`}
                  {...register("auctionDate", {
                    required: "Auction date is required",
                    validate: (v) =>
                      v >= todayISO || "Auction date cannot be in the past",
                  })}
                />
                {errors.auctionDate && (
                  <p className="form-error">{errors.auctionDate.message}</p>
                )}
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  <Clock className="w-4 h-4 inline mr-2" />
                  <span>
                    Start Time<span className="required">*</span>
                  </span>
                </label>
                <input
                  type="time"
                  className={`form-input ${
                    errors.auctionStartTime ? "form-input-error" : ""
                  }`}
{...register("auctionStartTime", {
                    required: "Start time is required",
                    validate: (v) => {
                      if (!watchAuctionDate) return true;

                      const selectedDate = new Date(watchAuctionDate);
                      if (!isToday(selectedDate)) return true;

                      return (
                        v >= format(new Date(), "HH:mm") ||
                        "Start time must be in the future for today's date"
                      );
                    },
                  })}                />
                {errors.auctionStartTime && (
                  <p className="form-error">
                    {errors.auctionStartTime.message}
                  </p>
                )}
              </div>
            </div>
            {isEdit ? (
              <div className="form-group">
                <label className="form-label">
                  <span>Auction Format<span className="required">*</span></span>
                </label>
                <select className="form-input" {...register("auctionMode")}>
                  <option value="reverse">Reverse Auction (lowest bid wins)</option>
                  <option value="forward">Forward Auction (highest bid wins)</option>
                </select>
              </div>
            ) : (
              <input type="hidden" {...register("auctionMode")} />
            )}
               {/* Second Row: Duration, Currency, Auction Type */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="form-group">
                <label className="form-label">
                  <Clock className="w-4 h-4 inline mr-2" />
                  <span>
                    Duration<span className="required">*</span>
                  </span>
                </label>
                <select className="form-input" {...register("duration")}>
                  <option value={15}>15 Minutes</option>
                  <option value={30}>30 Minutes</option>
                  <option value={60}>1 Hour</option>
                  <option value={120}>2 Hours</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span>₹/$</span>
                  <span className="ml-2">
                    Currency<span className="required">*</span>
                  </span>
                </label>
                <select className="form-input" {...register("currency")}>
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span>Auction Type<span className="required">*</span></span>
                </label>
                <select className="form-input" {...register("auctionType")}>
                  <option value="english_no_tie">
                    English Auction (No Tie)
                  </option>
                  <option value="english">English Auction</option>
                  <option value="traffic_light">
                    Traffic Light English Auction
                  </option>
                </select>
                <p className="auction-type-hint">
                  {watchAuctionType === "english_no_tie" &&
                    (isForwardAuction
                      ? "Standard auction — participants see the H1 (highest) bid value."
                      : "Standard auction — participants see the L1 (lowest) bid value.")}
                  {watchAuctionType === "english" &&
                    (isForwardAuction
                      ? "H1 value is hidden — participants see only rankings; each bid must beat that supplier's own highest bid by the increment."
                      : "L1 value is hidden — participants see only rankings; each bid must beat that supplier's own last bid by the decrement.")}
                  {watchAuctionType === "traffic_light" &&
                    (isForwardAuction
                      ? "No bid values shown — H1 & H2 appear yellow, H3 red; each bid must beat that supplier's own highest bid by the increment."
                      : "No bid values shown — L1 & L2 appear yellow, L3 red; each bid must beat that supplier's own last bid by the decrement.")}
                </p>
              </div>

            </div>
              {/* Third Row: Checkbox */}
          </div>
          </div>
          
          {/* Product Details */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-text-primary">
                Product Details
              </h2>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">
                  <FileText className="w-4 h-4 inline mr-2" />
                  <span>
                    Product Details / Description
                    <span className="required">*</span>
                  </span>
                </label>
                <textarea
                  rows={4}
                  className={`form-input ${
                    errors.auctionDetails ? "form-input-error" : ""
                  }`}
                  placeholder="Detailed description..."
                  {...register("auctionDetails", {
                    required: "Auction details/description is required"
                  })}
                />
                {errors.auctionDetails && (
                  <p className="form-error">{errors.auctionDetails.message}</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  {isForwardAuction ? (
                    <ArrowUp className="w-4 h-4 inline mr-2" />
                  ) : (
                    <ArrowDown className="w-4 h-4 inline mr-2" />
                  )}
                  <span>
                    {isForwardAuction ? "Incremental Value" : "Decremental Value"}
                    <span className="required">*</span>
                  </span>
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  className={`form-input ${
                    errors.decrementalValue ? "form-input-error" : ""
                  }`}
                  placeholder="Add Bid Amount"
                  {...register("decrementalValue", {
                    valueAsNumber: true,
                    required: isForwardAuction
                      ? "Incremental value is required"
                      : "Decremental value is required",
                    min: {
                      value: 1,
                      message: `${isForwardAuction ? "Incremental" : "Decremental"} value must be at least ₹1`,
                    },
                    max: {
                      value: 1000000,
                      message: `${isForwardAuction ? "Incremental" : "Decremental"} value cannot exceed ₹10,00,000`,
                    },
                  })}
                />
                {errors.decrementalValue && (
                  <p className="form-error">
                    {errors.decrementalValue.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Documents */}
        <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-text-primary">
                Auction Documents
              </h2>
              <p className="text-text-secondary">
                Optional – up to {MAX_FILES} files, {MAX_FILE_MB} MB
              </p>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">
                  <Upload className="w-4 h-4 inline mr-2" />
                  Upload Documents
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".jpeg,.jpg,.png,.pdf,.doc,.docx"
                  className="form-input"
                  onChange={handleFileUpload}
                  disabled={keptDocs.length + uploadedFiles.length >= MAX_FILES}
                />
              </div>
              {isEdit && existingDocs.length > 0 && (
                <div className="space-y-2 mt-4">
                  <h4 className="ap-newauction-file-list-title">Current Documents:</h4>
                  {existingDocs.map((doc) => {
                    const removed = removedDocIds.includes(doc.id);
                    return (
                      <div key={doc.id} className="ap-newauction-file-item" style={{ opacity: removed ? 0.5 : 1 }}>
                        <div className="file-info">
                          <FileText className="file-icon flex-shrink-0" />
                          <span
                            className="file-name truncate max-w-[40%] sm:max-w-none"
                            title={doc.file_name}
                            style={{ textDecoration: removed ? "line-through" : "none" }}
                          >
                            {doc.file_name}
                          </span>
                          {removed && <span className="file-size whitespace-nowrap">(will be removed)</span>}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setRemovedDocIds((prev) =>
                              removed ? prev.filter((x) => x !== doc.id) : [...prev, doc.id]
                            )
                          }
                          className={removed ? "btn btn-secondaryy" : "file-remove-btn flex-shrink-0"}
                          title={removed ? "Keep this document" : "Remove this document"}
                        >
                          {removed ? "Undo" : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
               {uploadedFiles.length > 0 && (
                <div className="space-y-2 mt-4">
                  <h4 className="ap-newauction-file-list-title">{isEdit ? "New Files:" : "Uploaded Files:"}</h4>
                  {uploadedFiles.map((file, idx) => (
                    <div
                      key={`${file.name}-${idx}`}
                      className="ap-newauction-file-item"
                    >
                      <div className="file-info">
                        <FileText className="file-icon flex-shrink-0" />
                        <span
                          className="file-name truncate max-w-[40%] sm:max-w-none"
                          title={file.name}
                        >
                          {file.name}
                        </span>
                        <span className="file-size whitespace-nowrap">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="file-remove-btn flex-shrink-0"
                        id="#dabba"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
            {/* Participants */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <div className="sachhu">
                  <h2 className="text-xl font-semibold text-text-primary">
                    Add Participants
                  </h2>
                  <p className="text-text-secondary mt-2">
                    Only invited participants can join. Add participants below.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    placeholder="Add participant email"
                    value={newParticipantEmail}
                    onChange={(e) =>
                      setNewParticipantEmail(e.target.value.replace(/\s/g, ""))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addParticipantByEmail();
                      }
                    }}
                    maxLength={254}
                    className="form-input"
                  />
                  <button
                    type="button"
                    onClick={addParticipantByEmail}
                    disabled={addingParticipants}
                    className="btn btn-primary"
                  > <Plus className="w-4 h-4" />
                    Add
                  </button>
                   <button
                    type="button"
                    onClick={() => setBulkOpen((s) => !s)}
                    className="btn btn-secondary"
                  >
                    <Plus className="w-4 h-4" />
                    Bulk
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body">
              {fields.length === 0 ? (
                <div className="ap-newauction-empty-state">
                  <Users className="w-12 h-12 text-text-secondary mx-auto mb-4" />
                  <p className="text-text-secondary">
                    No participants added yet.
                  </p>
                </div>
                    ) : (
                <div className="space-y-6">
                  {fields.map((field, idx) => (
                    <div
                      key={field.id}
                      className={`ap-newauction-participant-card ${
                        field._quick ? "quick-participant" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-text-primary">
                          Participant {idx + 1}
                        </h4>
                        <button
                          type="button"
                          onClick={() => remove(idx)}
                          className="file-remove-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* The email is typed once, in the Add box above; the card only
                            shows it. A hidden input keeps it in the form data. */}
                        <div className="form-group">
                          <label className="form-label">
                            {field._legacy ? "Contact Number" : "Email"}
                          </label>
                          <p className="font-medium text-text-primary" style={{ margin: 0, wordBreak: "break-all" }}>
                            {field._legacy ? field.contactNumber : field.mailId}
                          </p>
                          {!field._legacy && (
                            <input
                              type="hidden"
                              defaultValue={field.mailId}
                              {...register(`participants.${idx}.mailId`, {
                                setValueAs: (v: string) => String(v || "").trim().toLowerCase(),
                              })}
                            />
                          )}
                        </div>

                        <div className="form-group ap-participant-identity">
                          {field._known ? (
                            <div className="ap-participant-known">
                              <span className="ap-participant-badge">Registered user</span>
                              <strong>{field.personName || field.companyName}</strong>
                              {field.personName && field.companyName && (
                                <span className="ap-participant-company">{field.companyName}</span>
                              )}
                            </div>
                          ) : (
                            <div className="ap-participant-unknown">
                              {field._legacy
                                ? "Invited by phone number. They stay on this auction."
                                : "Not registered yet \u2014 they get the invite and reminders by email, and the auction appears once they register with this email."}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Company's 2nd auctioneer -- channel partners only */}
          {isChannelPartner && !isEdit && (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold text-text-primary">
                    Company's 2nd Auctioneer
                  </h2>
                  <span className="cv-badge">
                    <Eye className="w-3.5 h-3.5" />
                    View only
                  </span>
                </div>
                <p className="text-text-secondary mt-2">
                  Optional. Add the company's mobile number so they can follow this auction live.
                </p>
              </div>
              <div className="card-body">
                <CompanyViewerFields
                  value={viewer}
                  onChange={(next) => {
                    setViewer(next);
                    if (Object.keys(viewerErrors).length) setViewerErrors({});
                  }}
                  errors={viewerErrors}
                  disabled={isSubmitting}
                />
                <p className="cv-note">
                  They log in with this email (OTP, no password) and see only this
                  auction: the live countdown, bids and ranking. They can't bid, change the
                  auction, or open reports.
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="card">
            <div className="card-body flex items-center justify-end gap-2">
              {isEdit ? (
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/my-auction/${editId}`)}
                  className="btn btn-secondaryy"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setResetModal({ isOpen: true })}
                  className="btn btn-secondaryy"
                >
                  Reset
                </button>
              )}
                <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary"
              >
                {isSubmitting ? (
                  <div className="loading-spinner" />
                ) : (
                  <>{isEdit ? "Save Changes" : "Create Auction"}</>
                )}
              </button>
            </div>
          </div>
        </form>

        <SuccessModal
          isOpen={successModal.isOpen}
          onClose={() => {
            setSuccessModal({ isOpen: false, message: "" });
          }}
          title={isEdit ? "Auction Updated!" : "Auction Created!"}
          message={successModal.message}
        />

        <ErrorModal
          isOpen={errorModal.isOpen}
          onClose={() => {
            setErrorModal({ isOpen: false, message: "" });
          }}
          title="Error"
          message={errorModal.message}
        />

        <InfoModal
          isOpen={infoModal.isOpen}
          onClose={() => {
            setInfoModal({ isOpen: false, message: "" });
          }}
          title="Information"
          message={infoModal.message}
        />

        {/* Reset Confirmation Modal */}
        {resetModal.isOpen && (
          <div className="ap-modal-overlay">
            <div className="ap-modal">
              <div className="ap-modal-header">
                <h3 className="text-lg font-semibold">Confirm Reset</h3>
              </div>
              <div className="ap-modal-body">
                <p className="text-sm text-text-secondary">
                  Are you sure you want to reset the form? All entered data, including participants and uploaded files, will be lost.
                </p>
              </div>
              <div className="ap-modal-footer flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondaryy"
                  onClick={() => setResetModal({ isOpen: false })}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleReset}
                >
                  Yes, Reset
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Bulk Modal */}

{bulkOpen && (
          <div className="ap-modal-overlay">
            <div className="ap-modal">
              <div className="ap-modal-header">
                <h3 className="text-lg font-semibold">Bulk Add Participants</h3>
              </div>
              <div className="ap-modal-body">
                <p className="text-sm text-text-secondary mb-2">
                  Paste email addresses separated by newlines, commas or spaces.
                </p>
                <textarea
                  rows={6}
                  className="form-input w-full"
                  placeholder="Enter email addresses"
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                />
              </div>
                 <div className="ap-modal-footer flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={addingParticipants}
                  onClick={async () => {
                    const raw = bulkText || "";
                    const parts = raw
                      .split(/[,;\n\s]+/)
                      .map((p) => p.trim().toLowerCase())
                      .filter(Boolean);
                    const emails: string[] = [];
                    const exists = currentParticipantEmails();
                    for (const e of parts) {
                      if (!EMAIL_REGEX.test(e)) continue;
                      if (isOwnEmail(e)) continue;
                      if (exists.has(e) || emails.includes(e)) continue;
                      emails.push(e);
                    }
                    if (!emails.length) {
                      setInfoModal({
                        isOpen: true,
                        message:
                          "No valid or new email addresses found. Please check them and try again.",
                      });
                      return;
                    }
                    setAddingParticipants(true);
                    try {
                      const known = await lookupKnown(emails);
                      emails.forEach((email) =>
                        append(participantFromLookup(email, known))
                      );
                    } finally {
                      setAddingParticipants(false);
                    }
                    setBulkText("");
                    setBulkOpen(false);
                  }}
                >
                  Add All
                </button>
                <button
                  type="button"
                  className="btn btn-secondaryy"
                  onClick={() => setBulkText("")}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn btn-secondaryy"
                  onClick={() => setBulkOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  };

export default NewAuction;

